import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { action, computed, observable, observe, makeObservable } from "mobx";

/*
@deprecated The export below should be removed from here once everyone is using
import {SelectionMode} from "abreact/incubator/ui/SharedTypes";
instead of
import {SelectionMode} from "abreact/incubator/ui/manager/SelectionManager";
 */
export * from "abreact/incubator/ui/SharedTypes";

/**
 * The properties supported when constructing a selection manager.
 */

/**
 * A manager for tracking object selection.
 */
export let SelectionManager = (_class = class SelectionManager {
  constructor(props) {
    _defineProperty(this, "canSelectFunction", void 0);
    _defineProperty(this, "predicateFunction", void 0);
    _defineProperty(this, "_selectedItems", observable.array([], {
      deep: false
    }));
    makeObservable(this);
    this.canSelect = this.canSelect.bind(this);
    this.selectionMode = props.selectionMode || SelectionMode.SINGLE;
    this.canSelectFunction = props.canSelectFunction || (item => true);
    // TODO TEDDY - indexof doesn't always work with observable arrays apparently so add this optional predicate for
    // comparing ItemT's
    this.predicateFunction = props.predicateFunction || ((item1, item2) => item1 === item2);
  }
  canSelect(item) {
    return this.canSelectFunction(item);
  }
  areItemsEqual(item1, item2) {
    return this.predicateFunction(item1, item2);
  }

  /**
   * Registers a function to be called on a selection change.
   *
   * @param {() => void} onSelectionChange
   */
  observeSelectedItems(onSelectionChange) {
    // TODO This is going to leak if we do not use the baseMachine cleanup functions
    return observe(this._selectedItems, (change => onSelectionChange()).bind(this));
  }
  get selectionMode() {
    return this._selectionMode;
  }
  set selectionMode(mode) {
    // TODO This should reset the number of selected items when the mode changes
    this._selectionMode = mode;
  }

  /**
   * Returns the selected items.
   *
   * @returns An array of the selected items.
   */
  get selectedItems() {
    return this._selectedItems.slice();
  }
  set selectedItems(items) {
    if (!ArrayUtils.hasSameContents(items, this.selectedItems)) {
      this._selectedItems.replace(items);
    }
  }

  /**
   * Returns the first selected item, or null if none.
   *
   * @returns The first selected item, or null if none.
   */
  get selectedItem() {
    return this._selectedItems.length > 0 ? this._selectedItems[0] : undefined;
  }
  set selectedItem(item) {
    if (item !== this.selectedItem) {
      this._selectedItems.replace(item !== undefined ? [item] : []);
    }
  }

  /**
   * Returns true if the specified item is selected.
   *
   * @param item The item in question.
   * @returns {boolean} True if the specified item is selected.
   */
  isSelected(item) {
    return this._selectedItems.some(selectedItem => {
      return this.areItemsEqual(item, selectedItem);
    });
  }

  /**
   * Selects an item (perhaps not the best name for this method...)
   *
   * @param item The item that was selected.
   */
  onSelected(item) {
    if (this.selectionMode === SelectionMode.SINGLE) {
      let items = item !== undefined ? [item] : [];
      if (!ArrayUtils.hasSameContents(items, this.selectedItems)) {
        this._selectedItems.replace(items);
      }
    } else if (this.selectionMode === SelectionMode.MULTIPLE) {
      if (!this.isSelected(item)) {
        this._selectedItems.push(item);
      }
    }
  }

  /**
   * Deselects an item (perhaps not the best name for this method...)
   *
   * @param item The item that was deselected.
   */
  onDeselected(item) {
    const index = this._selectedItems.findIndex(selectedItem => this.areItemsEqual(item, selectedItem));
    if (index >= 0) {
      this._selectedItems.splice(index, 1);
    }
  }

  /**
   * Toggles the selected state of the specified item.
   *
   * Note: undefined clears the entire selection which is somewhat unexpected.
   *
   * TODO AndyA: consider removing undefined to clear the selection
   */
  toggleSelect(item) {
    if (item === undefined || this.selectionMode === SelectionMode.NONE) {
      this.clear();
    } else {
      const selected = this.isSelected(item);
      if (!selected) {
        this.onSelected(item);
      } else if (this.selectionMode === SelectionMode.MULTIPLE) {
        this.onDeselected(item);
      }
    }
  }

  /**
   * Clears the set of selected items.
   */
  clear() {
    if (this._selectedItems.length > 0) {
      this._selectedItems.clear();
    }
  }
}, _applyDecoratedDescriptor(_class.prototype, "selectedItems", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedItems"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectedItem", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onSelected", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onSelected"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onDeselected", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDeselected"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleSelect", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleSelect"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clear", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clear"), _class.prototype), _class);

/**
 * This class provides the current value from the selection manager to the ValuePicker.
 *
 * Note: this is the single-selection case.
 */
export class SelectionManagerValuePicker {
  constructor(selectionManager) {
    _defineProperty(this, "selectionManager", void 0);
    this.selectionManager = selectionManager;
  }
  get value() {
    return this.selectionManager.selectedItem;
  }
  observe(onValueSelected) {
    return this.selectionManager.observeSelectedItems(onValueSelected);
  }
}

/**
 * This class provides the current value from the selection manager to the ValuePicker.
 *
 * Note: this is the multiple-selection case.
 */
export class MultiSelectionManagerValuePicker {
  constructor(selectionManager) {
    _defineProperty(this, "selectionManager", void 0);
    this.selectionManager = selectionManager;
  }
  get values() {
    return this.selectionManager.selectedItems;
  }
}
//# sourceMappingURL=SelectionManager.js.map
